import { useState, useEffect } from "react";
import {
  dynamicSourceSchema,
  parseIntegrationRequest,
  integrationDataSourceSchema,
  parseProject,
  uid,
  type DynamicSource,
} from "@proof/core";
import { Button, Control } from "./components";
import type { EditorProps } from "./Configuration";

export function DynamicSourceManager({
  project,
  onChange,
  notify,
}: EditorProps) {
  const [requestText, setRequestText] = useState("");
  const [requestNotice, setRequestNotice] = useState("");
  const [draft, setDraft] = useState<DynamicSource>(),
    [selected, select] = useState(""),
    [optionText, setOptionText] = useState<Record<number, string>>({});
  useEffect(() => {
    setDraft(undefined);
    select("");
    setOptionText({});
  }, [project.id]);
  const sources = project.dynamicSources || [];
  const start = () => {
    setOptionText({});
    select("");
    setDraft(
      dynamicSourceSchema.parse({
        id: uid("source"),
        name: "New data source",
        parameters: [],
      }),
    );
  };
  const save = () => {
    try {
      const source = dynamicSourceSchema.parse(draft);
      if (sources.some((item) => item.id === source.id && item.id !== selected))
        throw new Error("Duplicate source key");
      function rebind<T extends { source?: { dynamicSourceId?: string } }>(
        field: T,
      ): T {
        return selected && field.source?.dynamicSourceId === selected
          ? {
              ...field,
              source: { ...field.source, dynamicSourceId: source.id },
            }
          : field;
      }
      onChange(
        parseProject({
          ...project,
          fields: project.fields.map(rebind),
          forms: project.forms.map((form) => ({
            ...form,
            fields: form.fields.map(rebind),
          })),
          parts: project.parts.map((part) => ({
            ...part,
            ...(part.dynamicSourcePolicies && selected && selected !== source.id
              ? {
                  dynamicSourcePolicies: Object.fromEntries(
                    Object.entries(part.dynamicSourcePolicies).map(
                      ([id, policy]) => [
                        id === selected ? source.id : id,
                        policy,
                      ],
                    ),
                  ),
                }
              : {}),
            overrides: Object.fromEntries(
              Object.entries(part.overrides).map(([id, patch]) => [
                id,
                rebind(patch),
              ]),
            ),
          })),
          dynamicSources: [
            ...sources.filter((item) => item.id !== selected),
            source,
          ],
        }),
      );
      select(source.id);
      setDraft(undefined);
    } catch {
      notify(
        "Use a source name, unique parameter keys and valid defaults/options.",
      );
    }
  };
  const referenced =
    (!!selected &&
      [
        ...project.fields,
        ...project.forms.flatMap((form) => form.fields),
        ...project.parts.flatMap((part) => Object.values(part.overrides)),
      ].some((field) => field.source?.dynamicSourceId === selected)) ||
    (!!selected &&
      project.parts.some((part) =>
        Object.hasOwn(part.dynamicSourcePolicies || {}, selected),
      ));
  return (
    <details className="panel padded">
      <summary>Dynamic source parameter forms</summary>
      <p>
        Define public inputs here, then select a Parameter form on each dynamic
        mapping. The host owns source URLs, credentials and system parameters.
      </p>
      <Control
        label="Import a URL or cURL request"
        note="Only declarations are parsed. Credentials are removed and no request is executed."
      >
        <textarea
          value={requestText}
          onChange={(event) => setRequestText(event.target.value)}
          placeholder="https://api.example.com/search?region=US"
        />
      </Control>
      <Button
        disabled={!requestText.trim()}
        onClick={() => {
          try {
            const { warnings, ...request } =
              parseIntegrationRequest(requestText);
            const id = uid("source");
            setDraft(
              dynamicSourceSchema.parse({
                id,
                name: new URL(request.endpoint).hostname,
                integration: integrationDataSourceSchema.parse({
                  ...request,
                  id,
                  name: new URL(request.endpoint).hostname,
                  mode: "http",
                  fields: [{ key: "value", label: "Response value", path: "" }],
                }),
                parameters: Object.entries(request.parameterDefaults).map(
                  ([key, value]) => ({
                    key,
                    label: key,
                    type:
                      typeof value === "number"
                        ? "number"
                        : typeof value === "boolean"
                          ? "checkbox"
                          : "text",
                    default: value,
                    required: false,
                  }),
                ),
              }),
            );
            select("");
            setRequestText("");
            setRequestNotice(
              [
                ...warnings,
                "Review the response mapping and register this declaration under Integration access. The trusted host must approve its permissions before it can supply form data.",
              ].join(" "),
            );
          } catch (error) {
            setRequestNotice((error as Error).message);
          }
        }}
      >
        Parse request into dynamic source
      </Button>
      {requestNotice && <p role="status">{requestNotice}</p>}
      <Button onClick={start}>Add parameter form</Button>
      <Control label="Edit parameter form">
        <select
          value={selected}
          onChange={(event) => {
            select(event.target.value);
            setOptionText({});
            setDraft(
              structuredClone(
                sources.find((source) => source.id === event.target.value),
              ),
            );
          }}
        >
          <option value="">Choose a form</option>
          {sources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>
      </Control>
      {selected && (
        <>
          <Button
            onClick={() => {
              setOptionText({});
              setDraft(
                structuredClone(
                  sources.find((source) => source.id === selected),
                ),
              );
            }}
          >
            Edit selected parameter form
          </Button>
          <Button
            disabled={referenced}
            onClick={() => {
              onChange(
                parseProject({
                  ...project,
                  dynamicSources: sources.filter(
                    (source) => source.id !== selected,
                  ),
                }),
              );
              select("");
              setDraft(undefined);
            }}
          >
            Delete parameter form
          </Button>
          {referenced && (
            <p className="muted">
              Unbind this parameter form from document, form and part mappings,
              and clear its part source requirements before deleting it.
            </p>
          )}
        </>
      )}
      {draft && (
        <div>
          <Control label="Integration style">
            <select
              value={
                draft.integration
                  ? draft.integration.httpMethod || "POST"
                  : "host"
              }
              onChange={(event) => {
                const mode = event.target.value;
                setDraft({
                  ...draft,
                  integration:
                    mode === "host"
                      ? undefined
                      : integrationDataSourceSchema.parse({
                          id: draft.id,
                          name: draft.name,
                          mode: "http",
                          endpoint:
                            draft.integration?.endpoint ||
                            "https://api.example.com/lookup",
                          requestStyle: "parameters",
                          httpMethod: mode,
                          fields: draft.integration?.fields || [
                            { key: "value", label: "Value", path: "" },
                          ],
                          parameterKeys: draft.parameters.map(
                            (parameter) => parameter.key,
                          ),
                        }),
                });
              }}
            >
              <option value="host">Host callback</option>
              <option value="GET">REST API · GET</option>
              <option value="POST">REST API · JSON POST</option>
            </select>
          </Control>
          {draft.integration && (
            <>
              <Control label="HTTPS endpoint">
                <input
                  value={draft.integration.endpoint || ""}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      integration: {
                        ...draft.integration!,
                        endpoint: event.target.value,
                      },
                    })
                  }
                />
              </Control>
              <details>
                <summary>Connector declaration for Integration access</summary>
                <textarea
                  readOnly
                  rows={10}
                  value={JSON.stringify(draft.integration, null, 2)}
                />
              </details>
            </>
          )}

          <label>
            <input
              type="checkbox"
              checked={draft.allowSkip === true}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  allowSkip: event.target.checked || undefined,
                })
              }
            />
            Allow this source to be skipped
          </label>
          <Control label="Parameter form key">
            <input
              value={draft.id}
              maxLength={100}
              onChange={(event) =>
                setDraft({ ...draft, id: event.target.value })
              }
            />
          </Control>
          <Control label="Parameter form name">
            <input
              value={draft.name}
              maxLength={100}
              onChange={(event) =>
                setDraft({ ...draft, name: event.target.value })
              }
            />
          </Control>
          {draft.parameters.map((parameter, index) => {
            const update = (patch: Partial<typeof parameter>) =>
              setDraft({
                ...draft,
                parameters: draft.parameters.map((item, i) =>
                  i === index ? { ...item, ...patch } : item,
                ),
              });
            return (
              <fieldset key={index}>
                <legend>Parameter {index + 1}</legend>
                <Control label={`Parameter ${index + 1} key`}>
                  <input
                    value={parameter.key}
                    maxLength={100}
                    onChange={(event) => update({ key: event.target.value })}
                  />
                </Control>
                <Control label={`Parameter ${index + 1} label`}>
                  <input
                    value={parameter.label}
                    maxLength={100}
                    onChange={(event) => update({ label: event.target.value })}
                  />
                </Control>
                <Control label={`Parameter ${index + 1} type`}>
                  <select
                    value={parameter.type}
                    onChange={(event) => {
                      setOptionText({});
                      update({
                        type: event.target.value as typeof parameter.type,
                        ...(event.target.value === "checkbox"
                          ? { choices: [], default: false }
                          : {}),
                      });
                    }}
                  >
                    {[
                      "text",
                      "number",
                      "email",
                      "phone",
                      "dropdown",
                      "radio",
                      "checkbox",
                    ].map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </Control>
                <Control label={`Parameter ${index + 1} whitespace`}>
                  <select
                    value={parameter.whitespace || "preserve"}
                    onChange={(event) =>
                      update({
                        whitespace: event.target.value as "preserve" | "trim",
                      })
                    }
                  >
                    <option value="preserve">Preserve whitespace</option>
                    <option value="trim">Trim surrounding whitespace</option>
                  </select>
                </Control>
                <label>
                  <input
                    type="checkbox"
                    checked={parameter.required}
                    onChange={(event) =>
                      update({ required: event.target.checked })
                    }
                  />
                  Required parameter
                </label>
                <Control label={`Parameter ${index + 1} default`}>
                  {parameter.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      checked={parameter.default === true}
                      onChange={(event) =>
                        update({ default: event.target.checked })
                      }
                    />
                  ) : (
                    <input
                      value={String(parameter.default ?? "")}
                      maxLength={1000}
                      onChange={(event) =>
                        update({ default: event.target.value })
                      }
                    />
                  )}
                </Control>
                {["dropdown", "radio"].includes(parameter.type) && (
                  <Control label={`Parameter ${index + 1} options`}>
                    <textarea
                      value={
                        Object.hasOwn(optionText, index)
                          ? optionText[index]
                          : parameter.choices
                              .map(
                                (choice) => `${choice.value}\t${choice.label}`,
                              )
                              .join("\n")
                      }
                      placeholder="value followed by a tab and label, one per line"
                      onChange={(event) => {
                        setOptionText((current) => ({
                          ...current,
                          [index]: event.target.value,
                        }));
                        update({
                          choices: event.target.value
                            .split("\n")
                            .filter(Boolean)
                            .map((line) => {
                              const [value, ...label] = line.split("\t");
                              return {
                                value,
                                label: label.join("\t") || value,
                              };
                            }),
                        });
                      }}
                    />
                  </Control>
                )}
                <Button
                  onClick={() => {
                    setOptionText({});
                    setDraft({
                      ...draft,
                      parameters: draft.parameters.filter(
                        (_, i) => i !== index,
                      ),
                    });
                  }}
                >
                  Remove parameter {index + 1}
                </Button>
              </fieldset>
            );
          })}
          <Button
            onClick={() => {
              let suffix = draft.parameters.length + 1;
              while (
                draft.parameters.some(
                  (parameter) => parameter.key === `parameter_${suffix}`,
                )
              )
                suffix++;
              setDraft({
                ...draft,
                parameters: [
                  ...draft.parameters,
                  {
                    key: `parameter_${suffix}`,
                    label: "Parameter",
                    type: "text",
                    default: "",
                    required: true,
                    choices: [],
                    help: "",
                  },
                ],
              });
            }}
          >
            Add parameter
          </Button>
          <Button onClick={save}>Save parameter form</Button>
          <Button onClick={() => setDraft(undefined)}>
            Cancel parameter edits
          </Button>
        </div>
      )}
    </details>
  );
}
